// Decides whether a stored Keycloak session can be resumed, or whether we must
// start a fresh login.
//
// The problem this exists for: the access token lives in a COOKIE and the
// refresh token lives in localStorage, and those two have different scoping
// rules. localStorage is keyed by ORIGIN, which includes the port. Cookies are
// keyed by host and ignore the port entirely.
//
// So with ds-agents on localhost:3000 and a second instance (a worktree, a
// branch preview) on localhost:5000, the second one reads the FIRST one's
// access_token cookie while seeing no refresh token of its own. keycloak-js is
// then handed a token it cannot refresh and belonging to a session it did not
// establish — and login fails in the least helpful way available: init throws,
// the catch sets initialized anyway, the app mounts unauthenticated, the first
// API call 401s, and client.js reloads the page straight back into Keycloak.
// A loop, with nothing in the server log but "No Bearer token in request".
//
// The cookie is not a mistake — Action Cable's WebSocket handshake cannot carry
// an Authorization header, so the token has to be somewhere the browser sends
// automatically. Given that, the pair simply has to be validated together.

/**
 * Returns `{ token, refreshToken }` when a stored session is worth resuming, or
 * `null` when Keycloak should be asked for a fresh login.
 *
 * Pure, so the rule is testable without a browser.
 */
export function resumableSession ({ token, refreshToken } = {}) {
  // Half a session is not a session. Passing a token with no refresh token
  // leaves keycloak-js unable to recover the moment that token expires, and
  // passing a refresh token with no access token is equally meaningless.
  if (!token || !refreshToken) return null

  // An access token whose expiry has already passed is not worth resuming
  // either; a clean login is faster than a refresh that will be rejected.
  if (isExpired(token)) return null

  return { token, refreshToken }
}

/**
 * True when a JWT's `exp` is in the past. Unreadable tokens count as expired:
 * a token we cannot parse is one we cannot reason about, and starting a fresh
 * login is always safe.
 */
export function isExpired (jwt, nowMs = Date.now()) {
  const exp = expiryMs(jwt)
  if (exp === null) return true
  return exp <= nowMs
}

function expiryMs (jwt) {
  const payload = decodePayload(jwt)
  if (!payload || typeof payload.exp !== 'number') return null
  return payload.exp * 1000
}

function decodePayload (jwt) {
  if (typeof jwt !== 'string') return null
  const parts = jwt.split('.')
  if (parts.length !== 3) return null

  try {
    // base64url → base64, then pad. atob rejects the url-safe alphabet.
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    return JSON.parse(atob(padded))
  } catch {
    return null
  }
}
